import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import lodash from 'lodash';
import classnames from 'classnames';
import { PageTitle, PageDescription } from 'components/common/Study/PageComponents';
import DefaultMaterialTable from 'common/util/materialTable';
import { Spinner } from 'components/common/LoadingStatus/LoadingStatus';
import { getRolesPermissions, savePermissions } from '../actions/permissions';
import { customTheme, customOptions, getColumns } from '../components/utils';
import { FixedFooter } from 'components/common/FixedFooter';
import CancelModal from 'pages/study/containers/CancelModal';
import { Button, BUTTON } from 'components/common/Button/Button';
import styles from './StudyPermissions.scss';
import { resourceTypes } from './index';
import { loadHomePageV4Access, newDoaStudyFlowAccess } from '../../../common/features/predicates';

const CANCEL_MODAL_TITLE = 'Are you sure you want to leave this page?';
const CANCEL_MODAL_DESCRIPTION = 'You made some edits to your study permissions but haven’t saved them yet.  Are you sure you want to leave without saving changes?';
const CANCEL_MODAL_ONCANCEL_BUTTON_TEXT = 'No, do not leave page';
const CANCEL_MODAL_ONSAVE_BUTTON_TEXT = 'Yes, leave page';

export const StudyPermissions = (props) => {
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [nextLocation, setNextLocation] = useState('');
    const [activeColumnId, setActiveColumnId] = useState('');
    const [disabled, setDisabled] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const dataRef = useRef(props.data);
    const unregisterLeaveHook = useRef();

    const updatePermissions = (action1, action2, data, item) => {
        const index1 = data.findIndex(i => i.action === action1);
        const index2 = data.findIndex(i => i.action === action2);

        if (index1 !== -1 && index2 !== -1) {
            const keysToUpdate = ['Team Member', 'Monitor', 'Admin', 'Viewer'];

            keysToUpdate.forEach(key => {
                data[index1][key] = item[key] || data[index1][key];
                data[index2][key] = item[key] || data[index2][key];
            });
        }
    };

    useEffect(() => {
        props.getRolesPermissions(resourceTypes.study);
    }, []);

    useEffect(() => {
        setLoading(true);

        let filterKey = [];

        props.data.forEach(item => {
            if (item.action === 'Manage Study Users') {
                updatePermissions('Manage Monitors', 'Manage Team', props.data, item);
            } else if (item.action === 'View Study Users') {
                updatePermissions('View Monitors', 'View Team', props.data, item);
            }
        });

        if (props.isNewDoaEnabled) {
            filterKey = ['Manage Study Users', 'View Study Users'];
        } else {
            filterKey = ['Manage Monitors', 'View Monitors', 'Manage Team', 'View Team'];
        }

        const filteredData = props.data.filter(item => !filterKey.includes(item.action));
        dataRef.current = props.data;
        let newData = filteredData.map(s => {
            let actionData = s.action;
            if (props.complion4FlagEnabled)
                switch (s.action) {
                    case 'Manage Study Permissions':
                        actionData = 'Manage Binder Permissions';
                        break;
                    case 'Manage Study Admins':
                        actionData = 'Manage Binder Admins';
                        break;
                    case 'Manage Study Metadata':
                        actionData = 'Manage Binder Metadata';
                        break;
                    case 'View Study Metadata':
                        actionData = 'View Binder Metadata';
                        break;
                    case 'Manage Study Tasks':
                        actionData = 'Manage Binder Tasks';
                        break;
                    case 'Manage Study Users':
                        actionData = 'Manage Binder Users';
                        break;
                    case 'View Study Users':
                        actionData = 'View Binder Users';
                        break;
                }
            return { ...s, displayName: actionData };
        });
        setData(newData);

        setLoading(false);
    }, [props.data, props.isNewDoaEnabled]);


    useEffect(() => {
        const { router, route } = props;
        unregisterLeaveHook.current = router.setRouteLeaveHook(route, routerWillLeave);
        return () => {
            unregisterLeaveHook.current();
        };
    }, [isEditing, disabled]);

    const editTable = (columnId) => {
        setIsEditing(true);
        setActiveColumnId(columnId);
    };

    const toggleCheckbox = (field, actionId) => {
        dataRef.current = dataRef.current.map(row => {
            if (row.id === actionId) {
                return {
                    ...row,
                    [field]: !row[field]
                };
            }
            return row;
        });
        const newData = data.map(row => {
            if (row.id === actionId) {
                return {
                    ...row,
                    [field]: !row[field]
                };
            }
            return row;
        });
        setData(newData);
        if (disabled) {
            setDisabled(false);
        }
    };

    const cancelChanges = () => {
        setIsEditing(false);
        setDisabled(true);
        dataRef.current = props.data;
    };

    const saveChanges = () => {
        setDisabled(true);
        setIsEditing(false);
        props.savePermissions(dataRef.current, activeColumnId, resourceTypes.study, props.isNewDoaEnabled);
        setActiveColumnId('');
    };

    const routerWillLeave = (location) => {
        if (!isEditing) {
            return true;
        }
        if (disabled) {
            return true;
        }
        setNextLocation(location.pathname);
        openCancelModal();
        return false;
    };

    const goToNextLocation = () => {
        const { router } = props;
        unregisterLeaveHook.current();
        if (nextLocation) {
            return router.push(nextLocation);
        }
    };

    const leavePage = () => {
        goToNextLocation();
    };

    const closeCancelModal = () => {
        setCancelModalOpen(false);
        setNextLocation('');
    };

    const openCancelModal = () => {
        setCancelModalOpen(true);
    };

    return (
        <div className={styles.studyPermissionsContainer} data-cy="StudyPermissions">
            <PageTitle>Permissions</PageTitle>
            <PageDescription >
                Manage {props.complion4FlagEnabled ? 'binder' : 'study'} permissions
            </PageDescription>
            <div className={classnames(styles.dashboardContainer, isEditing && styles.offset)} data-cy="StudyPermissions-div">
                {props.isLoading || loading ?
                    <Spinner actionLabel={'Loading'} itemName={'Permissions'} />
                    :
                    <DefaultMaterialTable
                        options={customOptions}
                        theme={customTheme}
                        columns={getColumns(props.columns, activeColumnId, isEditing, editTable, toggleCheckbox)}
                        data={data}
                        key={props.isLoading}
                    />
                }
            </div>
            {isEditing &&
                <FixedFooter className={styles.fixedFooter}>
                    <div className={styles.saveButtonContainer} data-cy="StudyPermissionsContainer">
                        <Button
                            className={styles.saveButton}
                            disabled={false}
                            bgcolor={BUTTON.BGCOLOR.WHITE}
                            color={BUTTON.COLOR.BLACK}
                            onClick={cancelChanges}
                            data-cy="StudyPermissions-Cancel">
                            Cancel
                        </Button>
                        <Button
                            className={styles.saveButton}
                            disabled={disabled}
                            bgcolor={BUTTON.BGCOLOR.ORANGE}
                            color={BUTTON.COLOR.WHITE}
                            onClick={saveChanges}
                            data-cy="StudyPermissions-Save">
                            Save
                        </Button>
                    </div>
                </FixedFooter>
            }
            <CancelModal
                isOpen={cancelModalOpen}
                onClose={closeCancelModal}
                onCancel={closeCancelModal}
                onSave={leavePage}
                title={CANCEL_MODAL_TITLE}
                helperText={CANCEL_MODAL_DESCRIPTION}
                onCancelText={CANCEL_MODAL_ONCANCEL_BUTTON_TEXT}
                onSaveText={CANCEL_MODAL_ONSAVE_BUTTON_TEXT}
            />
        </div>
    );
};

export default connect((state, props) => {
    const columns = lodash.get(state, 'me.institution.rolesPermissions.columns', []);
    const data = lodash.get(state, 'me.institution.rolesPermissions.data', []);
    const isLoading = lodash.get(state, 'me.institution.loadingRolesPermissions', true);
    const isNewDoaEnabled = newDoaStudyFlowAccess(state);
    // const isNewDoaEnabled = false;
    return {
        data,
        columns,
        isLoading,
        isNewDoaEnabled,
        complion4FlagEnabled: loadHomePageV4Access(state),
    };
}, {
    getRolesPermissions,
    savePermissions,
})(StudyPermissions);